import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCreateInfraAcquisitionPayment,
  getListInfraAcquisitionPaymentsQueryKey,
  getListInfraAcquisitionsQueryKey,
} from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { uploadPaymentProof } from "@/lib/upload";

const PAYMENT_METHODS = [
  "Cash",
  "UPI",
  "Bank_Transfer",
  "Card",
  "Cheque",
] as const;
const PAID_BY = ["Company", "Personal"] as const;

const today = () => new Date().toISOString().slice(0, 10);

interface InfraAddPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  acquisitionId: number;
  /** Total cost of the acquisition — used only for the Paid/Balance display in the list; not validated here. */
  totalCost: number;
  /** Sum of payments already made — shows in the dialog header for context. */
  paidSoFar: number;
}

interface PaymentForm {
  amount: string;
  paymentDate: string;
  paymentMethod: string;
  paidBy: string;
  paymentReference: string;
  paymentProofUrl: string | null;
}

const EMPTY_FORM: PaymentForm = {
  amount: "",
  paymentDate: today(),
  paymentMethod: "",
  paidBy: "",
  paymentReference: "",
  paymentProofUrl: null,
};

export function InfraAddPaymentDialog({
  open,
  onOpenChange,
  acquisitionId,
  totalCost,
  paidSoFar,
}: InfraAddPaymentDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createMutation = useCreateInfraAcquisitionPayment();

  const [form, setForm] = useState<PaymentForm>(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);

  function setF<K extends keyof PaymentForm>(key: K, value: PaymentForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleProofUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadPaymentProof(file);
      if (result.ok) {
        setF("paymentProofUrl", result.url);
        toast({ title: "Payment proof uploaded." });
      } else {
        toast({ title: result.message, variant: "destructive" });
      }
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const amount = Number(form.amount);
    if (!amount || amount <= 0) {
      toast({
        title: "Enter a valid amount greater than zero.",
        variant: "destructive",
      });
      return;
    }
    try {
      await createMutation.mutateAsync({
        acquisitionId,
        data: {
          amount,
          paymentDate: form.paymentDate,
          paymentMethod: form.paymentMethod || null,
          paidBy: form.paidBy || null,
          paymentReference: form.paymentReference || null,
          paymentProofUrl: form.paymentProofUrl || null,
        },
      });
      toast({ title: "Payment recorded." });
      queryClient.invalidateQueries({
        queryKey: getListInfraAcquisitionPaymentsQueryKey(acquisitionId),
      });
      queryClient.invalidateQueries({
        queryKey: getListInfraAcquisitionsQueryKey(),
      });
      setForm(EMPTY_FORM);
      onOpenChange(false);
    } catch {
      toast({ title: "Failed to record payment.", variant: "destructive" });
    }
  }

  function handleClose() {
    setForm(EMPTY_FORM);
    onOpenChange(false);
  }

  const balance = totalCost - paidSoFar;
  const inr = (n: number) =>
    `₹${n.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const isBusy = createMutation.isPending || uploading;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Payment</DialogTitle>
        </DialogHeader>

        {/* Running Paid / Balance summary */}
        <div className="rounded-md bg-muted/50 px-4 py-3 grid grid-cols-2 gap-2 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">Paid so far</p>
            <p className="font-semibold">{inr(paidSoFar)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Balance</p>
            <p className={`font-semibold ${balance > 0 ? "text-amber-700" : "text-green-700"}`}>
              {inr(Math.max(0, balance))}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Amount */}
          <div className="space-y-1.5">
            <Label>
              Amount (₹) <span className="text-destructive">*</span>
            </Label>
            <Input
              type="number"
              min="0.01"
              step="0.01"
              placeholder="e.g. 5000"
              value={form.amount}
              onChange={(e) => setF("amount", e.target.value)}
              required
            />
          </div>

          {/* Payment Date */}
          <div className="space-y-1.5">
            <Label>
              Payment Date <span className="text-destructive">*</span>
            </Label>
            <Input
              type="date"
              value={form.paymentDate}
              onChange={(e) => setF("paymentDate", e.target.value)}
              required
            />
          </div>

          {/* Payment Method */}
          <div className="space-y-1.5">
            <Label>Payment Method</Label>
            <Select
              value={form.paymentMethod || "__none__"}
              onValueChange={(v) =>
                setF("paymentMethod", v === "__none__" ? "" : v)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Not specified</SelectItem>
                {PAYMENT_METHODS.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m.replace(/_/g, " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Paid By */}
          <div className="space-y-1.5">
            <Label>Paid By</Label>
            <Select
              value={form.paidBy || "__none__"}
              onValueChange={(v) =>
                setF("paidBy", v === "__none__" ? "" : v)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Not specified</SelectItem>
                {PAID_BY.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reference */}
          <div className="space-y-1.5">
            <Label>Reference / TXN ID</Label>
            <Input
              placeholder="TXN12345"
              value={form.paymentReference}
              onChange={(e) => setF("paymentReference", e.target.value)}
            />
          </div>

          {/* Payment Proof Upload */}
          <div className="space-y-1.5">
            <Label>Payment Proof</Label>
            {form.paymentProofUrl ? (
              <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2">
                <a
                  href={form.paymentProofUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm underline truncate flex-1"
                >
                  View proof
                </a>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setF("paymentProofUrl", null)}
                >
                  Remove
                </Button>
              </div>
            ) : (
              <label className={`flex cursor-pointer items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm hover:bg-muted/50 ${uploading ? "pointer-events-none opacity-60" : ""}`}>
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                <span>{uploading ? "Uploading…" : "Upload payment proof"}</span>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*"
                  className="sr-only"
                  onChange={handleProofUpload}
                  disabled={uploading}
                />
              </label>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isBusy}>
              Cancel
            </Button>
            <Button type="submit" disabled={isBusy || !form.amount || !form.paymentDate}>
              {isBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Record Payment
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
